import React, { memo, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  ZoomControl,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from '../i18n';
import API_BASE_URL from '../config/api';

/** ✅ Fix típico de Leaflet en React (si alguna vez ves marcadores “rotos”) */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API_URL = API_BASE_URL;

const ElevationProfile = ({ elevationData, t }) => {
  if (!elevationData?.length) return null;

  const maxElevation = Math.max(...elevationData);
  const minElevation = Math.min(...elevationData);

  return (
    <div className="elevation-profile" style={{ marginTop: '20px' }}>
      <h3>{t('social.geoLocation.elevation.title')}</h3>
      <p>{t('social.geoLocation.elevation.max')}: {Math.round(maxElevation)} m</p>
      <p>{t('social.geoLocation.elevation.min')}: {Math.round(minElevation)} m</p>
    </div>
  );
};

const customIcon = (iconPath) =>
  new L.Icon({
    // ✅ en CRA/React, lo más seguro es PUBLIC_URL
    iconUrl: `${process.env.PUBLIC_URL}${iconPath.startsWith('/') ? '' : '/'}${iconPath}`,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  });

const FitBoundsToRoute = ({ route }) => {
  const map = useMap();

  useEffect(() => {
    if (!route?.length) return;

    // Si hay 1 solo punto, fitBounds no aporta mucho
    if (route.length === 1) {
      map.setView(route[0], Math.max(map.getZoom(), 14));
      return;
    }

    const bounds = L.latLngBounds(route);
    map.fitBounds(bounds, { padding: [20, 20] });
  }, [map, route]);

  return null;
};

const isFiniteNum = (v) => Number.isFinite(Number(v));
const isValidLatLon = (lat, lon) => isFiniteNum(lat) && isFiniteNum(lon);

const GeoLocationComponent = memo(
  ({
    fileId,
    height = 400,
    showElevation = true,
    // ✅ NUEVO: modo compacto para previews (chat, cards, etc.)
    compact = false,
  }) => {
    const { t } = useTranslation();

    const [data, setData] = useState(null);
    const [routeData, setRouteData] = useState([]);
    const [elevationRecords, setElevationRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async (fid, signal) => {
      setLoading(true);
      setError(null);

      try {
        console.log('📡 GeoLocationComponent → fetchData iniciado con fileId:', fid);

        const res = await axios.get(`${API_URL}/api/fit/parsed-data/${fid}`, { signal });
        const parsed = res.data;

        console.log('✅ Respuesta de la API:', parsed);
        setData(parsed);

        const records = Array.isArray(parsed?.records) ? parsed.records : [];

        // Elevación
        const elevations =
          records
            .map((r) => r?.enhanced_altitude ?? r?.elevation)
            .filter((e) => typeof e === 'number' && !Number.isNaN(e)) || [];

        setElevationRecords(elevations);
        console.log('📈 Elevaciones válidas encontradas:', elevations.length);

        // Ruta (lat/lon)
        const coordinates =
          records
            .filter((r) => isFiniteNum(r?.position_lat) && isFiniteNum(r?.position_long))
            .map((r) => [Number(r.position_lat), Number(r.position_long)])
            .filter(([lat, lon]) => isValidLatLon(lat, lon)) || [];

        setRouteData(coordinates);
        console.log('📍 Ruta trazada con', coordinates.length, 'puntos.');
      } catch (err) {
        // Si se aborta (cambio de fileId), no lo muestres como error
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;

        console.error('❌ Error al obtener los datos:', err);
        setError(t('social.geoLocation.errors.fetch'));
      } finally {
        setLoading(false);
      }
    }, [t]);

    useEffect(() => {
      if (!fileId) {
        setLoading(false);
        setError(t('social.geoLocation.errors.missingFileId'));
        return;
      }

      const controller = new AbortController();
      fetchData(fileId, controller.signal);

      return () => controller.abort();
    }, [fileId, fetchData, t]);

    if (loading) return <div>{t('social.geoLocation.loading')}</div>;
    if (error) return <div>{error}</div>;
    if (!data?.session && routeData.length === 0) {
      return <div>{t('social.geoLocation.emptySession')}</div>;
    }

    const session = data?.session || {};

    // ✅ Centro del mapa: usa start_position_* si existe, sino el primer punto de ruta
    const sessionStartLat = Number(session.start_position_lat);
    const sessionStartLon = Number(session.start_position_long);

    const fallbackLat = routeData?.[0]?.[0];
    const fallbackLon = routeData?.[0]?.[1];

    const centerLat = isValidLatLon(sessionStartLat, sessionStartLon) ? sessionStartLat : fallbackLat;
    const centerLon = isValidLatLon(sessionStartLat, sessionStartLon) ? sessionStartLon : fallbackLon;

    if (!isValidLatLon(centerLat, centerLon)) {
      return <div>{t('social.geoLocation.noCoordinates')}</div>;
    }

    const swcLat = Number(session.swc_lat);
    const swcLon = Number(session.swc_long);
    const necLat = Number(session.nec_lat);
    const necLon = Number(session.nec_long);

    const hasSWC = isValidLatLon(swcLat, swcLon) && !(swcLat === 0 && swcLon === 0);
    const hasNEC = isValidLatLon(necLat, necLon) && !(necLat === 0 && necLon === 0);

    // ✅ En compacto, por defecto no mostramos elevación (aunque venga showElevation=true)
    const effectiveShowElevation = showElevation && !compact;

    return (
      <div>
        <MapContainer
          center={[centerLat, centerLon]}
          zoom={13}
          style={{ height: `${height}px`, width: '100%' }}
          zoomControl={false}
          // ✅ NUEVO: en compacto desactivamos interacción para que no “robe” scroll en el chat
          scrollWheelZoom={!compact}
          dragging={!compact}
          doubleClickZoom={!compact}
          touchZoom={!compact}
          boxZoom={!compact}
          keyboard={!compact}
          // ✅ NUEVO: en compacto ocultamos atribución (opcional)
          attributionControl={!compact}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {routeData.length > 0 && (
            <>
              <Polyline positions={routeData} color="blue" />
              <FitBoundsToRoute route={routeData} />
            </>
          )}

          {/* ✅ En compacto NO ponemos markers para que el preview sea limpio */}
          {!compact && (
            <>
              {/* Marcador base (centro) */}
              <Marker position={[centerLat, centerLon]}>
                <Popup>{t('social.geoLocation.map.location')}: {centerLat}, {centerLon}</Popup>
              </Marker>

              {/* Inicio/Fin si existen */}
              {hasSWC && (
                <Marker position={[swcLat, swcLon]} icon={customIcon('/img/icons/start2.png')}>
                  <Popup>{t('social.geoLocation.map.startPoint')}</Popup>
                </Marker>
              )}

              {hasNEC && (
                <Marker position={[necLat, necLon]} icon={customIcon('/img/icons/stop2.png')}>
                  <Popup>{t('social.geoLocation.map.endPoint')}</Popup>
                </Marker>
              )}
            </>
          )}

          {/* ✅ En compacto puedes ocultar zoom si quieres (yo lo dejo igual) */}
          <ZoomControl position="topright" />
        </MapContainer>

      </div>
    );
  }
);

export default GeoLocationComponent;